import { Extension } from '@tiptap/core'
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion'
import { emailVariables, type EmailVariable } from '../../constants/variables'

export interface VariableSuggestionState {
  items: EmailVariable[]
  rect: DOMRect | null
  command: (variable: EmailVariable) => void
}

export interface VariableSuggestionHandlers {
  isOpen: () => boolean
  onStart: (state: VariableSuggestionState) => void
  onUpdate: (state: VariableSuggestionState) => void
  onExit: () => void
  onArrowDown: () => boolean
  onArrowUp: () => boolean
  onEnter: () => boolean
}

function filterVariables(query: string): EmailVariable[] {
  const q = query.toLowerCase()
  return emailVariables.filter(
    (v) =>
      v.key.toLowerCase().includes(q) || v.label.toLowerCase().includes(q) || v.description.toLowerCase().includes(q)
  )
}

export function createVariableSuggestion(handlers: VariableSuggestionHandlers): Extension {
  return Extension.create({
    name: 'variableSuggestion',

    addProseMirrorPlugins() {
      const options: Omit<SuggestionOptions<EmailVariable, EmailVariable>, 'editor'> = {
        char: '@',
        allowSpaces: false,
        allowedPrefixes: null,
        dismissOnOutsideClick: false,
        items: ({ query }) => filterVariables(query),
        command: ({ editor, range, props }) => {
          const code = props.key.replace(/\{\{|\}\}/g, '').trim()
          editor.chain().focus().deleteRange(range).insertVariable(code).run()
        },
        render: () => ({
          onStart: (props) =>
            handlers.onStart({
              items: props.items,
              rect: props.clientRect?.() ?? null,
              command: props.command
            }),
          onUpdate: (props) =>
            handlers.onUpdate({
              items: props.items,
              rect: props.clientRect?.() ?? null,
              command: props.command
            }),
          onKeyDown: (props) => {
            if (!handlers.isOpen()) return false
            switch (props.event.key) {
              case 'ArrowDown':
                return handlers.onArrowDown()
              case 'ArrowUp':
                return handlers.onArrowUp()
              case 'Enter':
              case 'Tab':
                return handlers.onEnter()
              case 'Escape':
                handlers.onExit()
                return true
              default:
                return false
            }
          },
          onExit: () => handlers.onExit()
        })
      }

      return [Suggestion({ editor: this.editor, ...options })]
    }
  })
}
